/**
 * Runtime-neutral host identity (见 18 / 01): which OS we run on, and which
 * release channel this build is.
 *
 * Both facts were previously readable only through the preload `hibroWindow`,
 * which does not exist in a browser — so module UI that picks a per-OS component
 * (`chat-header.darwin.tsx` / `.win32.tsx`) fell back to its default and
 * rendered the Windows variant on macOS. Neither fact is Electron-specific, so
 * each runtime's shell normalizes its own source into this one global:
 * Electron from `hibroWindow`, the browser page from the UA plus its build-time
 * channel injection.
 *
 * This is a *stamped global* rather than an injected bridge because its
 * consumers are module UI singletons and plain functions (zustand vanilla
 * stores, `.catch` handlers) that never see React props — the same reason
 * `hibroLog` and `hibroClipboard` are globals. The naming convention reserves
 * the `window.hibro*` namespace for exactly this purpose (见 naming-conventions
 * 第 1 层).
 *
 * The OS vocabulary lives in `@hibro/shared` so service / handshake / inline
 * UI read the same type. This file only stamps the global.
 */

import { type HostOs, normalizeHostOs } from '@hibro/shared';

export type { HostOs };
export { normalizeHostOs };

export type HostIdentity = NonNullable<Window['hibroHost']>;

/** Publish the identity for module UI and the shell's own bridges to read. */
export function stampHostIdentity(identity: HostIdentity): void {
  window.hibroHost = identity;
}

export function readHostIdentity(): HostIdentity | undefined {
  return window.hibroHost;
}
